// ============================================================================
// Safe Care - Événements clients : déclaration d'un événement (demande
// d'assistance / indemnisation) + justificatifs obligatoires (fichier -> DOCUMENT)
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / InputForm / SearchModal
//
// Deux modes d'utilisation :
//   - Route  : /assurance/demande-assistance/:idSouscription (souscription imposée)
//   - Embarqué (embedded) : page /indemnisation/creation ("Événements clients")
//     -> l'agent choisit le client, puis la souscription active
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { Form, Button, Spin, Tabs, Space, Table, Modal, Select, Upload, Input } from 'antd';
import { UploadOutlined, FilePdfOutlined, DeleteOutlined, EyeOutlined, UserOutlined, FileTextOutlined, ContainerFilled } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import SearchModal from '../../../Components/SearchModal';
import Params from '../../../Types/ParamsPicklist';
import {
  useGetAllSouscriptions,
  useGetSouscriptionsByClient,
  useGetEvenementsByVersion,
  useCreateDemandeAssistance,
  useGetAllDemandesAssistance,
  useGetJustificatifsByDemande,
  useAddJustificatif,
  useGetRegleEvenement,
  useGetDocumentsRequisBySouscription,
} from '../hooks';
import { DemandeAssistance, SouscriptionSafeCare, JustificatifDemande, EvenementCouvert, RegleEvenement, DocumentRequis } from '../types';

// Picklist client (table CLIENT existante) - l'agent agit pour le compte du client
const searchClient: Params = {
  CodeAgence: '',
  Table: 'CLIENT',
  Colonne1: 'NUMCLI',
  Colonne2: 'NOMCLI',
  Colagence: '101',
  Choix: 22,
};

interface Props {
  embedded?: boolean;
}

const DemandeAssistanceForm = ({ embedded = false }: Props) => {
  const { idSouscription } = useParams<{ idSouscription: string }>();
  const navigate = useNavigate();
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [justifsPending, setJustifsPending] = useState<JustificatifDemande[]>([]);
  const [savingJustif, setSavingJustif] = useState(false);
  const [previewJustif, setPreviewJustif] = useState<JustificatifDemande | null>(null);
  const [activeTab, setActiveTab] = useState('1');
  const [idDocumentRequisSel, setIdDocumentRequisSel] = useState<number | undefined>(undefined);
  const [docRequisSel, setDocRequisSel] = useState<DocumentRequis | null>(null);
  const [picklistDocOpen, setPicklistDocOpen] = useState(false);
  const [picklistDocSearch, setPicklistDocSearch] = useState('');
  const [picklistDocSelKeys, setPicklistDocSelKeys] = useState<React.Key[]>([]);
  const [picklistSousOpen, setPicklistSousOpen] = useState(false);

  // Sélections
  const [numcliSelected, setNumcliSelected] = useState<string>('');
  const [idSouscriptionSel, setIdSouscriptionSel] = useState<number | undefined>(undefined);
  const [idDemandeSel, setIdDemandeSel] = useState<number | undefined>(undefined);
  const [selectedDemandeKeys, setSelectedDemandeKeys] = useState<React.Key[]>([]);

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [enregActve, setEnregActve] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);

  // ---------- Souscription ----------
  const souscriptionIdRoute = !embedded && idSouscription ? parseInt(idSouscription) : undefined;
  const { data: allSouscriptions } = useGetAllSouscriptions();
  const { data: souscriptionsClient, isLoading: loadingSousClient } = useGetSouscriptionsByClient(
    embedded ? numcliSelected : undefined
  );

  const souscription: SouscriptionSafeCare | undefined = useMemo(() => {
    if (souscriptionIdRoute) {
      return (allSouscriptions ?? []).find((s) => s.IdSouscription === souscriptionIdRoute);
    }
    return (souscriptionsClient ?? []).find((s) => s.IdSouscription === idSouscriptionSel);
  }, [souscriptionIdRoute, allSouscriptions, souscriptionsClient, idSouscriptionSel]);

  const souscriptionsActives = (souscriptionsClient ?? []).filter((s) => s.Statut === 'ACTIVE');

  // Si le client n'a qu'une seule souscription active, la sélectionner automatiquement
  useEffect(() => {
    if (embedded && numcliSelected && !idSouscriptionSel && souscriptionsActives.length === 1) {
      setIdSouscriptionSel(souscriptionsActives[0].IdSouscription);
    }
  }, [embedded, numcliSelected, idSouscriptionSel, souscriptionsActives]);

  // En mode route, le client est celui de la souscription
  useEffect(() => {
    if (souscriptionIdRoute && souscription) {
      setNumcliSelected(souscription.Numcli);
      setIdSouscriptionSel(souscription.IdSouscription);
      form.setFieldValue('Numcli', souscription.Numcli);
      form.setFieldValue('NomClient', `${souscription.NOMCLI ?? ''} ${souscription.PRECLI ?? ''}`.trim());
    }
  }, [souscriptionIdRoute, souscription, form]);

  useEffect(() => {
    form.setFieldValue('IdSouscription', souscription?.IdSouscription);
    form.setFieldValue('NomProduit', souscription?.NomProduit ?? '');
    form.setFieldValue('IdEvenement', undefined);
  }, [souscription, form]);

  // ---------- Événements couverts par la version de la souscription ----------
  const { data: evenementsRes } = useGetEvenementsByVersion(souscription?.IdVersion);
  const evenements: EvenementCouvert[] =
    evenementsRes?.StatutCode === 200 ? evenementsRes.Result?.Table1 ?? [] : [];

  // ---------- Règle de l'événement sélectionné (pour le plafond) ----------
  const idEvenementSel = Form.useWatch('IdEvenement', form) as number | undefined;

  // ---------- Documents requis : filtrés par l'événement couvert (+ communs) ----------
  const { data: documentsRequisRes } = useGetDocumentsRequisBySouscription(souscription?.IdSouscription, 'INDEMNISATION', idEvenementSel);
  const documentsRequis: DocumentRequis[] =
    documentsRequisRes?.StatutCode === 200 ? documentsRequisRes.Result?.Table1 ?? [] : [];

  const montantDemande = Form.useWatch('MontantDemande', form) as number | undefined;
  const { data: regleEveRes } = useGetRegleEvenement(idEvenementSel);
  const regleEvenement: RegleEvenement | undefined =
    regleEveRes?.StatutCode === 200 ? regleEveRes.Result?.Table1?.[0] ?? regleEveRes.Result?.Table?.[0] : undefined;
  const plafondEvenement: number | undefined = regleEvenement?.MontantPlafond;

  // Avertissement en temps réel quand le montant dépasse le plafond
  useEffect(() => {
    if (montantDemande != null && plafondEvenement != null && montantDemande > plafondEvenement) {
      SweetAlert.fire({
        title: 'Montant supérieur au plafond',
        text: `Le montant saisi (${montantDemande}) dépasse le plafond configuré pour cet événement (${plafondEvenement}).`,
        icon: 'warning',
        confirmButtonText: 'Compris',
      });
    }
  }, [montantDemande, plafondEvenement]);

  // ---------- Événements déjà déclarés par le client ----------
  const { data: toutesDemandes, isLoading: loadingDemandes } = useGetAllDemandesAssistance();
  const demandesClient: DemandeAssistance[] = useMemo(
    () => (toutesDemandes ?? []).filter((d) => d.Numcli === numcliSelected),
    [toutesDemandes, numcliSelected]
  );
  const demandeSel = demandesClient.find((d) => d.IdDemandeAssistance === idDemandeSel);

  // ---------- Justificatifs ----------
  const createDemande = useCreateDemandeAssistance();
  const { data: justificatifsRes } = useGetJustificatifsByDemande(idDemandeSel);
  const addJustificatif = useAddJustificatif();
  const justificatifs: JustificatifDemande[] =
    justificatifsRes?.StatutCode === 200 ? justificatifsRes.Result?.Table1 ?? [] : [];

  // ---------- Actions ----------
  function initialiazeButton() {
    setAjoutActif(false);
    setEnregActve(true);
    setAnnulActive(true);
  }

  function SetStart() {
    if (!numcliSelected) {
      SweetAlert.fire({ title: "Sélectionnez d'abord un client", icon: 'warning' });
      return;
    }
    if (!souscription) {
      SweetAlert.fire({
        title: 'Souscription requise',
        text: souscriptionsActives.length === 0
          ? "Ce client n'a aucune souscription active."
          : 'Sélectionnez la souscription active du client.',
        icon: 'warning',
      });
      return;
    }
    setAjoutActif(true);
    setEnregActve(false);
    setAnnulActive(false);
    setIdDemandeSel(undefined);
    setSelectedDemandeKeys([]);
    setJustifsPending([]);
    form.setFieldsValue({ IdEvenement: undefined, DateSinistre: undefined, MontantDemande: undefined, Description: undefined });
    setActiveTab('1');
  }

  function handleAnulle() {
    form.setFieldsValue({ IdEvenement: undefined, DateSinistre: undefined, MontantDemande: undefined, Description: undefined });
    setJustifsPending([]);
    initialiazeButton();
    if (!embedded) navigate('/assurance/creation');
  }

  function handleClientSelect(code: string, libelle?: string) {
    setNumcliSelected(code ?? '');
    setIdSouscriptionSel(undefined);
    setIdDemandeSel(undefined);
    setSelectedDemandeKeys([]);
    setJustifsPending([]);
    form.setFieldValue('Numcli', code ?? '');
    // Récupère le nom depuis le libellé de la picklist si libelle n'est pas passé
    // (SearchModal appelle handleFunction avec 1 seul argument dans certains cas)
    const nom = libelle || form.getFieldValue('lib_NumcliPick') || '';
    form.setFieldValue('NomClient', nom);
    initialiazeButton();
  }

  const onSelectDemande = (keys: React.Key[]) => {
    setSelectedDemandeKeys(keys);
    const id = keys[0] as number | undefined;
    setIdDemandeSel(id);
    setJustifsPending([]);
    const d = demandesClient.find((x) => x.IdDemandeAssistance === id);
    if (d) {
      setIdSouscriptionSel(d.IdSouscription);
      form.setFieldsValue({
        IdEvenement: d.IdEvenement,
        DateSinistre: d.DateSinistre ? dayjs(d.DateSinistre) : undefined,
        MontantDemande: d.MontantDemande,
        Description: d.Description,
      });
    }
    initialiazeButton();
  };

  const onFinish = (values: any) => {
    if (!souscription) {
      SweetAlert.fire({ title: 'Souscription requise', icon: 'warning' });
      return;
    }
    if (plafondEvenement != null && values.MontantDemande != null && values.MontantDemande > plafondEvenement) {
      SweetAlert.fire({
        title: 'Montant supérieur au plafond',
        text: `Le montant demandé (${values.MontantDemande}) dépasse le plafond configuré (${plafondEvenement}). Veuillez corriger avant de soumettre.`,
        icon: 'warning',
      });
      return;
    }
    if (justifsPending.length === 0) {
      SweetAlert.fire({ title: 'Justificatif obligatoire', text: 'Ajoutez au moins un document justificatif avant d\'enregistrer.', icon: 'warning' });
      return;
    }
    // Vérification des documents obligatoires configurés manquants
    if (documentsRequis.length > 0) {
      const manquants = documentsRequis
        .filter((d) => d.Obligatoire)
        .filter((d) => !justifsPending.some((j) => j.IdDocumentRequis === d.IdDocumentRequis));
      if (manquants.length > 0) {
        SweetAlert.fire({
          title: 'Documents obligatoires manquants',
          text: `Veuillez fournir : ${manquants.map((m) => m.LibelleDocument).join(', ')}`,
          icon: 'warning',
        });
        return;
      }
    }
    SweetAlert.fire({
      title: "Déclaration d'événement",
      text: `Soumettre la demande d'indemnisation pour ${values.NomClient ?? souscription.Numcli} ?`,
      showCancelButton: true,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
      color: '#116599',
      confirmButtonColor: '#116599',
      cancelButtonColor: 'darkred',
      icon: 'question',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        const demande: DemandeAssistance = {
          IdSouscription: souscription.IdSouscription!,
          IdEvenement: values.IdEvenement,
          Numcli: souscription.Numcli,
          DateSinistre: values.DateSinistre,
          Description: values.Description,
          MontantDemande: values.MontantDemande,
          Statut: 'EN_COURS',
          UtiSai: user.coduti ?? '',
        };
        createDemande.mutate(demande, {
          onSuccess: (res) => {
            if (res.StatutCode === 200) {
              const idDemande = res.Result?.Table?.[0]?.IdDemandeAssistance;
              initialiazeButton();
              if (idDemande) {
                // Enregistrer avec la demande les justificatifs saisis pendant la déclaration
                const pendings = [...justifsPending];
                setJustifsPending([]);
                setIdDemandeSel(idDemande);
                setSelectedDemandeKeys([idDemande]);
                // Demande créée : débloquer le formulaire, les fichiers s'envoient en tâche de fond
                setLoading(false);
                setSavingJustif(true);
                Promise.allSettled(
                  pendings.map((j) =>
                    addJustificatif.mutateAsync({ ...j, IdDemandeAssistance: idDemande, UtiSai: user.coduti ?? '' })
                  )
                ).then((results) => {
                  setSavingJustif(false);
                  const ko = results.filter((r) => r.status === 'rejected' || r.value?.StatutCode !== 200).length;
                  const firstErr = results.find((r) => r.status === 'rejected' || r.value?.StatutCode !== 200);
                  const errDetail = firstErr?.status === 'rejected' ? String(firstErr.reason) : firstErr?.value?.Message;
                  SweetAlert.fire({
                    title: 'Événement enregistré',
                    text: ko > 0
                      ? `${pendings.length - ko} justificatif(s) enregistré(s), ${ko} en échec.${errDetail ? ` Détail : ${errDetail}` : ''}`
                      : `${pendings.length} justificatif(s) enregistré(s) avec la déclaration.`,
                    icon: ko > 0 ? 'warning' : 'success',
                  });
                });
              } else {
                setLoading(false);
                SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
              }
            } else {
              setLoading(false);
              SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
            }
          },
          onError: (err: any) => {
            setLoading(false);
            SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
          },
        });
      }
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    const labels: Record<string, string> = {
      IdEvenement: 'Événement couvert',
      DateSinistre: 'Date du sinistre',
      MontantDemande: 'Montant demandé',
      NomClient: 'Nom client',
    };
    const manquants = (errorInfo?.errorFields ?? [])
      .map((f: any) => labels[f.name?.[0]] ?? f.name?.[0] ?? 'Champ inconnu')
      .filter(Boolean);
    SweetAlert.fire({
      title: 'Champs obligatoires manquants',
      text: manquants.length > 0
        ? `Veuillez remplir : ${manquants.join(', ')}`
        : 'Veuillez remplir tous les champs obligatoires',
      icon: 'warning',
    });
  };

  // ---------- Justificatifs ----------
  const peutAjouterJustif = !!demandeSel && demandeSel.Statut === 'EN_COURS';

  const fileToDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  // Upload direct dans le formulaire : en déclaration -> liste en attente ;
  // sur un événement EN_COURS sélectionné -> enregistrement immédiat.
  const handleUploadJustif = async (file: File) => {
    if (!ajoutActve && !peutAjouterJustif) {
      SweetAlert.fire({
        title: idDemandeSel
          ? `Statut ${demandeSel?.Statut} : ajout impossible.`
          : "Cliquez d'abord sur « Ajouter » pour démarrer la déclaration.",
        icon: 'warning',
      });
      return;
    }
    if (file.size / 1024 > 2000) {
      SweetAlert.fire({ title: 'Fichier trop volumineux', text: `"${file.name}" dépasse 2 Mo.`, icon: 'warning' });
      return;
    }
    const justif: JustificatifDemande = {
      IdDemandeAssistance: idDemandeSel ?? 0,
      Description: file.name,
      PieceJointe: await fileToDataUri(file),
      NomFichier: file.name,
      IdDocumentRequis: docRequisSel?.IdDocumentRequis,
      LibelleDocument: docRequisSel?.LibelleDocument,
    };
    // Réinitialise la sélection du label après l'upload pour afficher la picklist à nouveau
    setDocRequisSel(null);
    setIdDocumentRequisSel(undefined);
    if (!idDemandeSel) {
      setJustifsPending((prev) => [...prev, justif]);
      return;
    }
    setSavingJustif(true);
    addJustificatif.mutate({ ...justif, UtiSai: user.coduti ?? '' }, {
      onSuccess: (res) => {
        setSavingJustif(false);
        if (res?.StatutCode !== 200) {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      },
      onError: () => setSavingJustif(false),
    });
  };

  // ---------- Colonnes ----------
  const columnsDemandes: ColumnsType<DemandeAssistance> = [
    { title: 'N°', dataIndex: 'IdDemandeAssistance', width: 60, align: 'center' },
    { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
    { title: 'Événement', dataIndex: 'LibelleEvenement', width: 150 },
    { title: 'Date sinistre', dataIndex: 'DateSinistre', width: 110, render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY') : '') },
    { title: 'Montant demandé', dataIndex: 'MontantDemande', width: 120, align: 'right' },
    { title: 'Justif.', dataIndex: 'NbJustificatifs', width: 70, align: 'center' },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
    { title: 'Motif rejet', dataIndex: 'MotifRejet', ellipsis: true },
  ];

  // Pendant la déclaration : justificatifs saisis en attente ; sinon ceux de la demande sélectionnée
  const justifsAffiches: JustificatifDemande[] = idDemandeSel ? justificatifs : justifsPending;

  // ---------- Onglets ----------
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: "Déclaration d'événement",
      children: (
        <div className="tabItem">
          <div>
            <Box fontStyle="inherit" className="border border-left" width="100%" position={embedded ? 'relative' : 'absolute'} sx={{ p: 1 }}>
              <legend>Événement du client</legend>
              <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
                <Form.Item name="IdSouscription" hidden><input type="hidden" /></Form.Item>
                <Form.Item name="Numcli" hidden><input type="hidden" /></Form.Item>

                <Box fontStyle="inherit">
                  {embedded ? (
                    <SearchModal
                      width={12}
                      Titre=""
                      label="Client (NUMCLI)"
                      name="NumcliPick"
                      Parametre={searchClient}
                      Taille1={167}
                      Taille2={320}
                      form={form}
                      handleFunction={handleClientSelect}
                    />
                  ) : null}
                  <InputForm width={9} label="Nom client" name="NomClient" type="label" />
                  {embedded ? (
                    /* Picklist "Souscription active" — même UX que la picklist
                       "Type de document" : icône de recherche -> modale avec
                       les souscriptions actives du client (radio) */
                    <Form.Item label="Souscription active" required style={{ marginBottom: 4 }}>
                      <Space.Compact block>
                        <Input.Search
                          placeholder="Rechercher..."
                          readOnly
                          value={idSouscriptionSel != null ? String(idSouscriptionSel) : ''}
                          onSearch={() => {
                            if (!numcliSelected) {
                              SweetAlert.fire({ title: "Sélectionnez d'abord un client", icon: 'warning' });
                              return;
                            }
                            setPicklistSousOpen(true);
                          }}
                          disabled={!numcliSelected}
                          style={{ width: 167 }}
                        />
                        <Input
                          disabled
                          placeholder={numcliSelected ? 'Sélectionnez la souscription' : "Sélectionnez d'abord un client"}
                          value={souscription ? `${souscription.NomProduit} v${souscription.NumeroVersion} (${souscription.MontantCotisation})` : ''}
                          style={{ width: 320 }}
                        />
                      </Space.Compact>
                    </Form.Item>
                  ) : (
                    <InputForm width={9} label="Produit" name="NomProduit" type="label" />
                  )}
                  <Form.Item
                    name="IdEvenement"
                    label="Événement couvert"
                    rules={[{ required: true, message: 'Événement obligatoire' }]}
                  >
                    <Select
                      disabled={!ajoutActve}
                      placeholder={souscription ? "Sélectionnez l'événement" : "Sélectionnez d'abord une souscription"}
                      options={evenements.map((e) => ({ value: e.IdEvenement, label: `${e.Code} - ${e.Libelle}` }))}
                    />
                  </Form.Item>
                  <InputForm width={9} label="Date du sinistre" name="DateSinistre" type="date" disabled={!ajoutActve} />
                </Box>
                <hr />
                <Box fontStyle="inherit">
                  <InputForm width={9} money label="Montant demandé" name="MontantDemande" type="number" disabled={!ajoutActve} />
                  {plafondEvenement != null && (
                    <div style={{ fontSize: 12, color: 'var(--darkColor)', marginBottom: 8, marginTop: -4 }}>
                      Plafond configuré pour cet événement : <b>{plafondEvenement}</b>
                    </div>
                  )}
                  <InputForm width={9} label="Description" name="Description" type="TextArea" placeholder="Décrivez l'événement..." disabled={!ajoutActve} notRequired />
                  {/* Justificatifs obligatoires : uploadés dans le formulaire, enregistrés avec l'événement */}
                  <Form.Item label="Justificatifs (documents) *" required>
                    {/* Picklist des labels de documents requis configurés pour la version */}
                    {documentsRequis.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {/* Tags de statut des documents requis */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                          {documentsRequis.map((d) => {
                            const fourni = justifsAffiches.some((j) => j.IdDocumentRequis === d.IdDocumentRequis);
                            return (
                              <span
                                key={d.IdDocumentRequis}
                                title={d.Description}
                                style={{
                                  fontSize: 11,
                                  padding: '2px 8px',
                                  borderRadius: 10,
                                  border: `1px solid ${fourni ? 'var(--darkColor)' : '#d4380d'}`,
                                  background: fourni ? 'var(--lightColor002)' : '#fff1f0',
                                  color: fourni ? 'var(--darkColor)' : '#d4380d',
                                }}
                              >
                                {d.Obligatoire ? '* ' : ''}
                                {d.LibelleDocument}
                                {fourni ? ' ✓' : ' ✗'}
                              </span>
                            );
                          })}
                        </div>
                        {/* Picklist (style SearchModal) pour sélectionner le type de document */}
                        {!docRequisSel && (
                          <Form.Item label="Type de document (label)" style={{ marginBottom: 4 }}>
                            <Space.Compact block>
                              <Input.Search
                                placeholder="Rechercher..."
                                value={picklistDocSearch}
                                onChange={(e) => setPicklistDocSearch(e.target.value)}
                                onSearch={() => setPicklistDocOpen(true)}
                                disabled={(!ajoutActve && !peutAjouterJustif) || savingJustif}
                                style={{ width: 167 }}
                              />
                              <Input
                                disabled
                                placeholder="Sélectionnez le type de document"
                                value=""
                                style={{ width: 320 }}
                              />
                            </Space.Compact>
                          </Form.Item>
                        )}
                        {/* Champ upload qui apparaît après sélection du label */}
                        {docRequisSel && (
                          <div style={{
                            padding: 12,
                            borderRadius: 8,
                            border: '1px solid var(--colorOpaque030)',
                            background: 'var(--lightColor001)',
                            marginBottom: 8,
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <div>
                                <b style={{ color: 'var(--darkColor)' }}>{docRequisSel.LibelleDocument}</b>
                                {docRequisSel.Obligatoire && <span style={{ color: '#d4380d' }}> *</span>}
                                {docRequisSel.Description && (
                                  <div style={{ fontSize: 11, color: '#666' }}>{docRequisSel.Description}</div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => { setDocRequisSel(null); setIdDocumentRequisSel(undefined); }}
                                style={{
                                  border: 'none', background: 'transparent',
                                  color: '#ff4d4f', cursor: 'pointer', fontSize: 16,
                                }}
                                title="Annuler la sélection"
                              >
                                ✕
                              </button>
                            </div>
                            <Upload.Dragger
                              accept=".pdf,.jpg,.jpeg,.png"
                              showUploadList={false}
                              disabled={(!ajoutActve && !peutAjouterJustif) || savingJustif}
                              beforeUpload={(file) => { handleUploadJustif(file); return false; }}
                            >
                              <div style={{ padding: '4px 0' }}>
                                <UploadOutlined style={{ fontSize: 22, color: '#116599' }} />
                                <div style={{ fontSize: 12, marginTop: 4, color: '#8c8c8c' }}>
                                  Uploadez le fichier pour "{docRequisSel.LibelleDocument}" (PDF, JPG, PNG — max 2 Mo)
                                </div>
                              </div>
                            </Upload.Dragger>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Upload libre si aucun document requis configuré */}
                    {documentsRequis.length === 0 && (
                      <Upload.Dragger
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png"
                        showUploadList={false}
                        disabled={(!ajoutActve && !peutAjouterJustif) || savingJustif}
                        beforeUpload={(file) => { handleUploadJustif(file); return false; }}
                      >
                        <div style={{ padding: '4px 0' }}>
                          <UploadOutlined style={{ fontSize: 22, color: '#116599' }} />
                          <div style={{ fontSize: 12, marginTop: 4, color: '#8c8c8c' }}>
                            Cliquez ou glissez les documents ici (PDF, JPG, PNG — max 2 Mo chacun)
                          </div>
                        </div>
                      </Upload.Dragger>
                    )}
                    {/* Vignettes de prévisualisation sous le champ */}
                    <div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {justifsAffiches.map((j, i) => {
                          const isPdf = j.NomFichier?.toLowerCase().endsWith('.pdf');
                          return (
                            <div key={j.IdJustificatif ?? i} style={{ width: 92, textAlign: 'center', position: 'relative' }}>
                              {j.PieceJointe && !isPdf ? (
                                <img
                                  src={j.PieceJointe}
                                  alt={j.NomFichier}
                                  onClick={() => setPreviewJustif(j)}
                                  style={{ width: 92, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid #d9d9d9', cursor: 'pointer' }}
                                />
                              ) : (
                                <div
                                  onClick={() => j.PieceJointe && setPreviewJustif(j)}
                                  style={{ width: 92, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff5f5', cursor: j.PieceJointe ? 'pointer' : 'default' }}
                                >
                                  <FilePdfOutlined style={{ fontSize: 28, color: '#d4380d' }} />
                                </div>
                              )}
                              <div style={{ fontSize: 10, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.NomFichier}>
                                {j.NomFichier}
                              </div>
                              {j.LibelleDocument && (
                                <div style={{ fontSize: 9, color: 'var(--darkColor)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.LibelleDocument}>
                                  {j.LibelleDocument}
                                </div>
                              )}
                              {!j.IdJustificatif && (
                                <DeleteOutlined
                                  onClick={() => setJustifsPending((p) => p.filter((_x, idx) => idx !== i))}
                                  style={{ position: 'absolute', top: 2, right: 2, color: '#ff4d4f', background: '#fff', borderRadius: '50%', padding: 2, cursor: 'pointer' }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {ajoutActve && justifsAffiches.length === 0 && (
                        <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>
                          Au moins un document justificatif est obligatoire
                        </div>
                      )}
                    </div>
                  </Form.Item>
                </Box>
              </Box>
            </Box>
          </div>
        </div>
      ),
    },
    // {
    //   key: '2',
    //   label: 'Événements déclarés',
    //   children: (
    //     <div className="tabItem">
    //       <div>
    //         <Box fontStyle="inherit" width="85%" position={embedded ? 'relative' : 'absolute'} sx={{ p: 1 }}>
    //           <legend>Événements déclarés du client</legend>
    //           <Spin spinning={loadingDemandes}>
    //             <Table
    //               size="small"
    //               style={{ width: '100%' }}
    //               rowKey="IdDemandeAssistance"
    //               rowSelection={{ type: 'radio', selectedRowKeys: selectedDemandeKeys, onChange: onSelectDemande }}
    //               columns={columnsDemandes}
    //               dataSource={demandesClient}
    //               scroll={{ y: 300, x: 10 }}
    //               pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20], showSizeChanger: true, position: ['bottomRight'] }}
    //               locale={{ emptyText: numcliSelected ? 'Aucun événement déclaré' : "Sélectionnez d'abord un client" }}
    //             />
    //           </Spin>
    //         </Box>
    //       </div>
    //     </div>
    //   ),
    // },
  ];

  return (
    <div className={embedded ? undefined : 'tab safeCarePage'} style={embedded ? { position: 'relative', marginTop: 40 } : undefined}>
      {!embedded && (
        <Typography align="center" className="formTitle">
          <b>{window.location.pathname}</b>
        </Typography>
      )}
      <Spin spinning={loading} size="large" style={{ color: '#116599' }}>
        <Form
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className="bG" style={embedded ? { top: 'auto', marginTop: 0 } : undefined}>
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" disabled={ajoutActve} onClick={SetStart} type="ghost" htmlType="button" block>
                  Ajouter
                </Button>
                <Button size="large" className="bt_add" disabled={enregActve} type="ghost" htmlType="submit" block>
                  Enregistrer
                </Button>
                <Button size="large" disabled={annulActive} onClick={handleAnulle} className="bt_add" type="ghost" htmlType="button" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs size="small" type="card" activeKey={activeTab} onChange={setActiveTab} items={items} />
            </div>
          </div>
        </Form>
      </Spin>

      {/* Picklist des types de documents requis — Dialog style SearchModal identique au champ Client */}
      <Dialog
        className="modal"
        onClick={() => setPicklistDocOpen(false)}
        sx={{ '& .MuiDialog-paper': { minWidth: 'sm', maxHeight: 'md' } }}
        maxWidth="md"
        open={picklistDocOpen}
        onClose={() => { setPicklistDocOpen(false); setPicklistDocSearch(''); setPicklistDocSelKeys([]); }}
      >
        <DialogTitle>Sélectionnez le type de document</DialogTitle>
        <DialogContent dividers></DialogContent>
        <Spin spinning={false} tip="En Cour de Traitement ..." style={{ color: '#116599' }}>
          <Stack direction="row" spacing={5}>
            <Form name="picklistDocForm" wrapperCol={{ span: 16 }} style={{ minWidth: 600, marginLeft: 50 }} initialValues={{ remember: true }}>
              <Tabs defaultActiveKey="3" items={[
                {
                  key: '1',
                  label: <span id="Tab"><Typography><UserOutlined />IDENTIFIANT</Typography></span>,
                  children: <InputForm notRequired name="text" type="text" label="IDENTIFIANT" />,
                },
                {
                  key: '2',
                  label: <span id="Tab"><Typography><FileTextOutlined />LIBELLE</Typography></span>,
                  children: <InputForm notRequired name="text" type="text" label="LIBELLE" />,
                },
                {
                  key: '3',
                  label: <span id="Tab"><Typography><ContainerFilled />ALL</Typography></span>,
                  children: (
                    <Box fontStyle="inherit" display="flex">
                      <InputForm
                        noupercase
                        taille={600}
                        autoFocus
                        notRequired
                        name="text"
                        type="text"
                        label="TEXT"
                        onChange={(e: any) => setPicklistDocSearch(e.target.value.trim())}
                      />
                      <hr />
                      <Button style={{ top: '10px' }}>Rechercher</Button>
                    </Box>
                  ),
                },
              ]} />
            </Form>
          </Stack>
          <DialogContent dividers>
            <Table
              style={{ width: 'auto' }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: picklistDocSelKeys,
                onChange: (keys: React.Key[], rows: any[]) => {
                  setPicklistDocSelKeys(keys);
                  if (rows[0]) {
                    const d = rows[0] as DocumentRequis;
                    setDocRequisSel(d);
                    setIdDocumentRequisSel(d.IdDocumentRequis);
                    setPicklistDocOpen(false);
                    setPicklistDocSearch('');
                    setPicklistDocSelKeys([]);
                  }
                },
              }}
              scroll={{ y: 250, x: 10 }}
              rowKey="IdDocumentRequis"
              columns={[
                { title: 'Code', dataIndex: 'Ordre', width: 60 },
                {
                  title: 'Libelle',
                  dataIndex: 'LibelleDocument',
                  filteredValue: picklistDocSearch ? [picklistDocSearch] : [],
                  onFilter: (value: any, record: any) =>
                    String(record.LibelleDocument).toLowerCase().includes(String(value).toLowerCase()),
                  render: (text: string, record: DocumentRequis) => (
                    <span>
                      {text}
                      {record.Obligatoire && <span style={{ color: '#d4380d', marginLeft: 6 }}>*</span>}
                    </span>
                  ),
                },
                { title: 'Description', dataIndex: 'Description', ellipsis: true },
              ]}
              dataSource={documentsRequis.filter((d) => {
                const fourni = justifsAffiches.some((j) => j.IdDocumentRequis === d.IdDocumentRequis);
                return !fourni;
              })}
            />
          </DialogContent>
        </Spin>
        <DialogActions>
          <Button type="default" danger onClick={() => { setPicklistDocOpen(false); setPicklistDocSearch(''); setPicklistDocSelKeys([]); }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Picklist des souscriptions actives du client — même UX que la picklist "Type de document" */}
      <Dialog
        className="modal"
        sx={{ '& .MuiDialog-paper': { minWidth: 'sm', maxHeight: 'md' } }}
        maxWidth="md"
        open={picklistSousOpen}
        onClose={() => setPicklistSousOpen(false)}
      >
        <DialogTitle>Sélectionnez la souscription active</DialogTitle>
        <DialogContent dividers></DialogContent>
        <Spin spinning={loadingSousClient} tip="En Cour de Traitement ..." style={{ color: '#116599' }}>
          <DialogContent dividers>
            <Table
              style={{ width: 'auto' }}
              size="small"
              rowKey="IdSouscription"
              rowSelection={{
                type: 'radio',
                selectedRowKeys: idSouscriptionSel != null ? [idSouscriptionSel] : [],
                onChange: (keys: React.Key[]) => {
                  setIdSouscriptionSel(keys[0] as number | undefined);
                  setIdDemandeSel(undefined);
                  setSelectedDemandeKeys([]);
                  setPicklistSousOpen(false);
                },
              }}
              onRow={(record) => ({
                onClick: () => {
                  setIdSouscriptionSel(record.IdSouscription);
                  setIdDemandeSel(undefined);
                  setSelectedDemandeKeys([]);
                  setPicklistSousOpen(false);
                },
                style: { cursor: 'pointer' },
              })}
              scroll={{ y: 250, x: 10 }}
              pagination={{ defaultPageSize: 6, position: ['bottomRight'] }}
              locale={{ emptyText: numcliSelected ? 'Aucune souscription active pour ce client' : "Sélectionnez d'abord un client" }}
              columns={[
                { title: 'N°', dataIndex: 'IdSouscription', width: 70 },
                { title: 'Produit', dataIndex: 'NomProduit' },
                { title: 'Version', dataIndex: 'NumeroVersion', width: 80, align: 'center' },
                { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 110, align: 'right' },
                { title: 'Statut', dataIndex: 'Statut', width: 90 },
              ]}
              dataSource={souscriptionsActives}
            />
          </DialogContent>
        </Spin>
        <DialogActions>
          <Button type="default" danger onClick={() => setPicklistSousOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prévisualisation du justificatif — centrée, au-dessus de tout, border-radius 12px */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            {previewJustif?.NomFichier ?? 'Aperçu du document'}
          </div>
        }
        open={!!previewJustif}
        onCancel={() => setPreviewJustif(null)}
        footer={null}
        width="90%"
        centered
        zIndex={2100}
        className="safeCareModal"
        style={{ maxWidth: '1400px' }}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
      >
        {previewJustif?.PieceJointe && (
          <div style={{ textAlign: 'center' }}>
            {previewJustif.NomFichier?.toLowerCase().endsWith('.pdf') ? (
              <iframe
                src={previewJustif.PieceJointe}
                title="Aperçu"
                style={{ width: '100%', height: '75vh', border: '1px solid var(--colorOpaque030)', borderRadius: 10 }}
              />
            ) : (
              <img
                src={previewJustif.PieceJointe}
                alt="Justificatif"
                style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 10, border: '1px solid var(--colorOpaque030)' }}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DemandeAssistanceForm;
